/**
 * Ask-AI / Build-with-AI (S9) — one-shot "propose tiles" request over the
 * EXISTING ai-chat IPC transport (`window.electronAPI.aiChat`), NOT the
 * ai-chat zustand store or `useAiChatSession` (those are coupled to a real
 * workspace query/notebook/plsql tab and a persistent multi-turn session;
 * this is a single fire-and-forget structured request per click, so it talks
 * to the same `ai-chat:send-message` / `ai-chat:stream` / `ai-chat:done`
 * channels directly with a synthetic per-call tabId).
 *
 * Nothing here executes SQL — it only asks the AI for proposals and parses
 * the response. Applying/adding a proposal (and its preview) is the caller's
 * job, which is what keeps this read-only-by-construction.
 */
import { useCallback, useRef, useState } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { buildDashboardAiRequest, parseTileProposals, type TileProposal } from '../services/aiTileProposal';

export type DashboardAiProposeState =
  | { status: 'idle' }
  | { status: 'running'; step: 'schema' | 'drafting' }
  | { status: 'done'; proposals: TileProposal[] }
  | { status: 'error'; message: string };

export type DashboardAiProposeArgs = {
  connectionId: string;
  connectionAlias: string;
  engine: string;
  database?: string;
  prompt: string;
  mode: 'single' | 'multi';
};

export function useDashboardAiPropose() {
  const [state, setState] = useState<DashboardAiProposeState>({ status: 'idle' });
  const runIdRef = useRef(0);
  const activeTabIdRef = useRef<string | null>(null);
  const { track } = useAnalytics();

  const reset = useCallback(() => {
    runIdRef.current++;
    setState({ status: 'idle' });
  }, []);

  const propose = useCallback(
    async (args: DashboardAiProposeArgs) => {
      const runId = ++runIdRef.current;
      const api = window.electronAPI;
      if (!api?.aiChat || !api?.dashboards) {
        setState({ status: 'error', message: 'AI chat is unavailable.' });
        return;
      }

      setState({ status: 'running', step: 'schema' });
      let schemaContext: { tables: string[]; truncated: boolean };
      try {
        schemaContext = await api.dashboards.getAiSchemaContext(args.connectionId, args.database);
      } catch {
        schemaContext = { tables: [], truncated: false };
      }
      if (runId !== runIdRef.current) return;

      setState({ status: 'running', step: 'drafting' });
      const tabId = `dashboard-ai-${crypto.randomUUID()}`;
      activeTabIdRef.current = tabId;
      const { systemPrompt, message } = buildDashboardAiRequest({
        connectionAlias: args.connectionAlias,
        engine: args.engine,
        database: args.database,
        schemaContext,
        prompt: args.prompt,
        mode: args.mode,
      });

      let text = '';
      const cleanupStream = api.aiChat.onStream((data) => {
        if (data.tabId !== tabId) return;
        if (data.event.type === 'text') text += data.event.text;
      });

      const outcome = await new Promise<{ ok: true } | { ok: false; message: string }>((resolve) => {
        const cleanupDone = api.aiChat!.onDone((data) => {
          if (data.tabId !== tabId) return;
          cleanupDone();
          cleanupError();
          resolve({ ok: true });
        });
        const cleanupError = api.aiChat!.onError((data) => {
          if (data.tabId !== tabId) return;
          cleanupDone();
          cleanupError();
          resolve({ ok: false, message: data.message });
        });
        api.aiChat!.sendMessage({ tabId, message, sessionId: null, systemPrompt, adapterName: null }).catch(
          (err: unknown) => {
            cleanupDone();
            cleanupError();
            resolve({ ok: false, message: err instanceof Error ? err.message : String(err) });
          },
        );
      });
      cleanupStream();
      activeTabIdRef.current = null;
      if (runId !== runIdRef.current) return;

      if (!outcome.ok) {
        setState({ status: 'error', message: outcome.message });
        return;
      }
      const parsed = parseTileProposals(text);
      if (!parsed.ok) {
        setState({ status: 'error', message: parsed.error });
        return;
      }
      setState({ status: 'done', proposals: parsed.proposals });
      track('feature:dashboard_ai_propose', { count: parsed.proposals.length });
    },
    [track],
  );

  const cancel = useCallback(() => {
    runIdRef.current++;
    const tabId = activeTabIdRef.current;
    if (tabId) window.electronAPI?.aiChat?.cancel(tabId).catch(() => {});
    activeTabIdRef.current = null;
    setState({ status: 'idle' });
  }, []);

  return { state, propose, reset, cancel };
}
